/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { Picker } from "@spectrum-web-components/picker";
import { useRef, CSSProperties, useEffect } from "react";
import { Placement } from "@spectrum-web-components/overlay";
import * as React from "react";

export type SharedPickerOptionValue = string;

export interface SharedPickerOption {
  name: string;
  value: SharedPickerOptionValue;
}

export interface SharedPickerProps {
  options: SharedPickerOption[];
  selectedValue: SharedPickerOptionValue;
  onOptionSelected: (selectedOptionValue: SharedPickerOptionValue) => void;
  isQuiet?: boolean;
  pickerSize?: "s" | "m" | "l" | "xl";
  pickerStyles?: CSSProperties;
  ariaLabel?: string;
  disabled?: boolean;
  placement?: Placement;
}

export const SharedPicker: React.FC<SharedPickerProps> = ({
  options,
  selectedValue,
  onOptionSelected,
  isQuiet = false,
  pickerSize = "m",
  pickerStyles = { marginTop: 8 },
  ariaLabel,
  disabled = false,
  placement = "bottom",
  // Default styles
}) => {
  const pickerRef = useRef<Picker>(null);

  useEffect(() => {
    const pickerElement = pickerRef.current;
    if (!pickerElement) return;

    const handleChange = (event: Event): void => {
      const picker = event.target as Picker;
      onOptionSelected(picker.value);
    };

    pickerElement.addEventListener("change", handleChange);
    return (): void => {
      pickerElement.removeEventListener("change", handleChange);
    };
  }, [onOptionSelected]);

  const selectedOption = options.find(
    (option) => option.value === selectedValue
  );

  useEffect(() => {
    if (pickerRef.current) {
      pickerRef.current.disabled = disabled;
      const existingStyle = pickerRef.current.querySelector("style");

      if (existingStyle) {
        existingStyle.remove();
      }

      const style = document.createElement("style");
      if (disabled) {
        style.textContent = `
          sp-picker[disabled] {
            --spectrum-picker-text-color: var(--spectrum-global-color-gray-900);
            --spectrum-picker-text-color-disabled: var(--spectrum-global-color-gray-900);
            opacity: 0.5;
          }
        `;
      } else if (isQuiet) {
        style.textContent = `
          sp-picker::part(button) {
            background-color: var(--spectrum-global-color-gray-100);
            border: 1px solid var(--spectrum-global-color-gray-300);
            border-radius: 4px;
            color: var(--spectrum-global-color-gray-900);
            margin-block-start: 0;
          }
          sp-picker::part(button):hover {
            background-color: var(--spectrum-global-color-gray-200);
          }
        `;
      }

      if (style.textContent) {
        pickerRef.current.appendChild(style);
      }
    }
  }, [disabled, isQuiet]);

  return isQuiet ? ( // Conditional rendering based on isQuiet prop - the presence whether true or false seems to enforce truthy value
    <sp-picker
      key={selectedValue}
      size={pickerSize}
      label={selectedOption?.name}
      value={selectedOption?.value}
      ref={pickerRef}
      style={pickerStyles}
      quiet={isQuiet}
      aria-label={ariaLabel}
      placement={placement}
      title={ariaLabel}
    >
      <div slot="tooltip" style={{ display: "none" }}>
        {ariaLabel}
      </div>
      {options.map((option) => (
        <sp-menu-item key={option.value} value={option.value}>
          {option.name}
        </sp-menu-item>
      ))}
    </sp-picker>
  ) : (
    <sp-picker
      key={selectedValue}
      size={pickerSize}
      label={selectedOption?.name}
      value={selectedOption?.value}
      ref={pickerRef}
      style={pickerStyles}
      placement={placement}
      title={ariaLabel}
    >
      <div slot="tooltip" style={{ display: "none" }}>
        {ariaLabel}
      </div>
      {options.map((option) => (
        <sp-menu-item key={option.value} value={option.value}>
          {option.name}
        </sp-menu-item>
      ))}
    </sp-picker>
  );
};
